import { useMemo } from 'react';
import { css } from '@emotion/react';
import { EventStatus } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/calendar_event.pb';
import { Icon } from '@frontend/icons';
import { ScheduleRequestEventData } from '@frontend/schedule-helpers';
import { theme } from '@frontend/theme';
import { rgba, Text } from '@frontend/design-system';
import { PRIMARY_EVENT_COLOR } from '../../../../constants';
import { EventCardComponent } from '../../../schedule-calendar-components/event-card';
import { getNonAttendedEventStatusIconName } from '../../calendar-view-v3/helper';
import { OverlappingEventIndicatorVariantType } from '../../calendar-view-v3/overlapping-event-indicator';
import { getDurationFromHours, checkIsNonAttendedEventStatus } from '../../utils';
import { GridEventCardBase } from '../atoms/grid-event-card-base';

type GridAppointmentCardProps = {
  id: string;
  title: string;
  startDate: string;
  startHour: string;
  endHour: string;
  locationName?: string;
  locationId?: string;
  entityId?: string;
  entityName?: string;
  personStatus?: string;
  eventStatus?: EventStatus;
  eventTypeColor?: string;
  backgroundColor?: string;
  overlappingIndicatorVariant?: OverlappingEventIndicatorVariantType;
  isYesterdayEvent?: boolean;
  scheduleRequestEventData?: ScheduleRequestEventData;
};

//#region Card

export const GridAppointmentCard = ({
  id,
  title,
  startDate,
  startHour,
  endHour,
  locationName = '',
  locationId,
  entityId,
  entityName,
  personStatus,
  eventStatus,
  eventTypeColor,
  backgroundColor = theme.colors.white,
  overlappingIndicatorVariant,
  isYesterdayEvent,
  scheduleRequestEventData,
}: GridAppointmentCardProps) => {
  const duration = useMemo(() => getDurationFromHours(startHour, endHour), [startHour, endHour]);
  const primaryColor = eventTypeColor ?? PRIMARY_EVENT_COLOR;
  const isNonAttendedEvent = checkIsNonAttendedEventStatus(eventStatus);

  return (
    <GridEventCardBase
      primaryColor={primaryColor}
      backgroundColor={backgroundColor}
      isNonAttendedEvent={isNonAttendedEvent}
      isSelected={false}
    >
      <NonAttendedEventWrapper isNonAttendedEvent={isNonAttendedEvent} isSelected={false}>
        <NonAttendedEventStatusIcon eventStatus={eventStatus} />
        <GridEventCardBase.ArticleShell>
          <GridEventCardBase.PersonStatusBar personStatus={personStatus} />
          <GridEventCardBase.EventColoredLine />
          <EventCardComponent
            id={id}
            eventType='appointment'
            title={title}
            duration={duration}
            locationName={locationName}
            locationId={locationId}
            providerId={entityId}
            providerName={entityName}
            isExpanded={false}
            startDate={startDate}
            overlappingIndicatorVariant={overlappingIndicatorVariant}
            isYesterdayEvent={isYesterdayEvent}
            scheduleRequestEventData={scheduleRequestEventData}
          />
        </GridEventCardBase.ArticleShell>
      </NonAttendedEventWrapper>
    </GridEventCardBase>
  );
};

//#endregion

//#region Tooltip

type GridAppointmentTooltipProps = {
  title: string;
  startHour: string;
  endHour: string;
  appointmentType?: string;
};

export const GridAppointmentTooltip = ({ title, startHour, endHour, appointmentType }: GridAppointmentTooltipProps) => {
  const duration = useMemo(() => getDurationFromHours(startHour, endHour), [startHour, endHour]);

  return (
    <div css={appointmentTooltipStyle}>
      <Text weight='bold' size='medium' color='white'>
        {title}
      </Text>
      {!!appointmentType && (
        <Text size='small' color='white'>
          {appointmentType}
        </Text>
      )}
      <Text size='small' color='white'>
        {duration}
      </Text>
    </div>
  );
};

const appointmentTooltipStyle = {
  display: 'grid',
  gap: theme.spacing(0.25),
};

//#endregion

// V3-specific components for non-attended events
const NonAttendedEventStatusIcon = ({ eventStatus }: { eventStatus?: EventStatus }) => {
  const eventStatusIconName = getNonAttendedEventStatusIconName(eventStatus);
  if (!eventStatusIconName) return null;

  return (
    <Icon
      name={eventStatusIconName}
      color='error'
      size={16}
      css={{
        position: 'absolute',
        top: theme.spacing(0.5),
        right: theme.spacing(0.5),
        transition: 'opacity 0.2s ease-in-out',
      }}
    />
  );
};

const nonAttendedEventHoverStyle = css({
  background: theme.colors.white,
  borderColor: 'transparent',
  '& > article': {
    opacity: 0.5,
  },
  '& > svg': {
    opacity: 0,
  },
});

const NonAttendedEventWrapper = ({
  children,
  isNonAttendedEvent,
  isSelected = false,
}: {
  children: React.ReactNode;
  isNonAttendedEvent: boolean;
  isSelected?: boolean;
}) => {
  if (!isNonAttendedEvent) {
    return <>{children}</>;
  }

  return (
    <div
      css={css({
        background: rgba(theme.colors.critical20, 0.2),
        border: `1px dashed ${theme.colors.critical50}`,
        borderRadius: theme.borderRadius.small,
        transition: 'all 0.3s cubic-bezier(0.7, -0.4, 0.4, 1.4)',
        height: '100%',

        '&:hover, &:focus-visible': {
          ...nonAttendedEventHoverStyle,
        },
        ...(isSelected
          ? {
              background: theme.colors.white,
              borderColor: 'transparent',
            }
          : {}),
      })}
    >
      {children}
    </div>
  );
};
